import React from 'react';
import { Box, Grid } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import localeUtils from 'utils/LanguageLocale';
import { fontSizes, fontWeights, space } from 'style/config';
import InputMsg from 'components/Inputs/InputMsg/InputMsg';
import { wrapFunctionalComponent } from 'utils/framework';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import TaxFormValidator from 'components/RichProfile/MyAccount/TaxClaim/utils/TaxFormValidator';
import FormValidator from 'utils/FormValidator';
import dateUtils from 'utils/Date';

const {
    ERROR_CODES: {
        ISSUE_DATE_EMPTY,
        EXPIRATION_DATE_EMPTY,
        DATE_RANGE_INVALID,
        ISSUE_DATE_OUT_OF_BOUNDS,
        EXPIRATION_DATE_OUT_OF_BOUNDS,
        EXPIRATION_DATE_INVALID
    }
} = ErrorConstants;

const IssueExpirationDateComponent = props => {
    const { issueDate, expirationDate, formErrors } = props;
    const selectedCategory = props.selectedCategory.toLowerCase();

    const appendAddressDataToStepFourReduxData = (category, objectKey, value) => {
        props.updateStep4Data(category, objectKey, value);
    };

    const getText = localeUtils.getLocaleResourceFile('components/RichProfile/MyAccount/TaxClaim/locales', 'TaxClaim');

    const hasEmptyIssueDateError = Boolean(formErrors?.issueDateErrors?.issueDateEmpty);
    const hasExpirationDateError = Boolean(
        formErrors?.expirationDateErrors?.expirationDateEmpty || formErrors?.expirationDateErrors?.expirationDateInvalid
    );
    const hasOutOfBoundsIssueDateError = Boolean(formErrors?.issueDateErrors?.issueDateOutOfBounds);
    const hasOutOfBoundsExpirationDateError = Boolean(formErrors?.expirationDateErrors?.expirationDateOutOfBounds);
    const hasError = Boolean(hasEmptyIssueDateError || hasExpirationDateError);

    const emptyIssueDateMsg = hasEmptyIssueDateError && ErrorsUtils.getMessage(ISSUE_DATE_EMPTY);
    const emptyExpDateMsg = formErrors?.expirationDateErrors?.expirationDateEmpty
        ? ErrorsUtils.getMessage(EXPIRATION_DATE_EMPTY)
        : formErrors?.expirationDateErrors?.expirationDateInvalid
            ? ErrorsUtils.getMessage(EXPIRATION_DATE_INVALID)
            : null;
    const outOfBoundsIssueDateMsg = hasOutOfBoundsIssueDateError && ErrorsUtils.getMessage(ISSUE_DATE_OUT_OF_BOUNDS);
    const outOfBoundsExpDateMsg = hasOutOfBoundsExpirationDateError && ErrorsUtils.getMessage(EXPIRATION_DATE_OUT_OF_BOUNDS);

    const handleInitialDateChange = event => {
        const value = dateUtils.handleDateFormat(event.target.value);
        const issueDateChanged = 'issueDate';
        props.handleInitialDateChange(value);
        appendAddressDataToStepFourReduxData(selectedCategory, issueDateChanged, dateUtils.formatQueryDate(value));

        if (value) {
            props.clearErrors('issueDateErrors', ISSUE_DATE_EMPTY);

            const isInvalid = TaxFormValidator.validateIssueDate(value);

            if (!isInvalid) {
                props.clearErrors('issueDateErrors', ISSUE_DATE_OUT_OF_BOUNDS);
            }
        }
    };

    const handleEndDateChange = event => {
        const value = dateUtils.handleDateFormat(event.target.value);
        const localExpDate = 'expirationDate';
        props.handleEndDateChange(value);
        appendAddressDataToStepFourReduxData(selectedCategory, localExpDate, dateUtils.formatQueryDate(value));

        if (value && props.issueDate) {
            props.clearErrors('expirationDateErrors', EXPIRATION_DATE_EMPTY);

            const isInvalid = TaxFormValidator.validateExpirationDate(value, issueDate);

            if (!isInvalid) {
                props.clearErrors('expirationDateErrors', DATE_RANGE_INVALID);
                props.clearErrors('expirationDateErrors', EXPIRATION_DATE_INVALID);
                props.clearErrors('expirationDateErrors', EXPIRATION_DATE_OUT_OF_BOUNDS);
            }
        }
    };

    return (
        <Box
            marginTop={2}
            marginBottom={4}
        >
            {hasError && (
                <InputMsg
                    role={hasError ? 'alert' : null}
                    color={hasError ? 'error' : null}
                    children={getText(DATE_RANGE_INVALID)}
                    css={styles.invalidDate}
                />
            )}
            <Grid
                gap={4}
                gridTemplateColumns='1fr 1fr'
            >
                <TextInput
                    css={styles.inputDate}
                    onChange={handleInitialDateChange}
                    placeholder={getText('placeholder')}
                    label={getText('issueDateInputLabel')}
                    value={issueDate}
                    onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                    onPaste={FormValidator.pasteAcceptOnlyNumbers}
                    invalid={hasEmptyIssueDateError || hasOutOfBoundsIssueDateError}
                    message={hasEmptyIssueDateError ? emptyIssueDateMsg : hasOutOfBoundsIssueDateError ? outOfBoundsIssueDateMsg : null}
                    marginBottom={2}
                    maxLength={TaxFormValidator.FIELD_LENGTHS.date}
                />

                <TextInput
                    css={styles.inputDate}
                    onChange={handleEndDateChange}
                    placeholder={getText('placeholder')}
                    label={getText('expirationDateInputLabel')}
                    onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                    onPaste={FormValidator.pasteAcceptOnlyNumbers}
                    value={expirationDate}
                    invalid={hasExpirationDateError || hasOutOfBoundsExpirationDateError}
                    message={hasExpirationDateError ? emptyExpDateMsg : hasOutOfBoundsExpirationDateError ? outOfBoundsExpDateMsg : null}
                    marginBottom={2}
                    maxLength={TaxFormValidator.FIELD_LENGTHS.date}
                />
            </Grid>
        </Box>
    );
};

const styles = {
    inputDate: {
        width: '45%'
    },
    idCardLabel: {
        fontSize: fontSizes.md,
        fontWeight: fontWeights.bold
    },
    invalidDate: {
        fontSizes: fontSizes.base,
        lineHeight: '18px',
        marginBottom: space[4]
    }
};

export default wrapFunctionalComponent(IssueExpirationDateComponent, 'IssueExpirationDateComponent');
